import { FC, Key, useState } from "react";
import {
  Button,
  Divider,
  TablePaginationConfig,
  Typography,
  Form,
} from "antd";
import {
  CreateWrapper,
  StyledEmailWrapper,
  StyledInput,
  StyledSelect,
  StyledTable,
  StyledTitle,
} from "./styled";
import { CopyableText } from "../CopyableText";
import { EditableCell } from "./EditableCell";
import { ArrowDown } from "../icons/ArrowDown";
import { IDataTable } from "../../types/contractors";

interface IProps {
  pagination: false | TablePaginationConfig | undefined;
  table: IDataTable[];
  setTable: React.Dispatch<React.SetStateAction<IDataTable[]>>;
}

export const ContractorsTable: FC<IProps> = ({
  pagination,
  table,
  setTable,
}) => {
  const [isOpenCreateRow, setIsOpenCreateRow] = useState(false);
  const [editingKey, setEditingKey] = useState("");
  const [editform] = Form.useForm();
  const [createform] = Form.useForm();

  const onFinish = (values: any) => {
    setTable([...table, values]);
    closeModal();
  };

  const closeModal = () => {
    createform.resetFields();
    setIsOpenCreateRow(false);
  };

  const options = [
    { label: "КЖ", value: "КЖ" },
    { label: "ОВ", value: "ОВ" },
    { label: "ХС", value: "ХС" },
    { label: "СГП", value: "СГП" },
  ];

  const isEditing = (record: IDataTable) => record.key === editingKey;

  const edit = (record: Partial<IDataTable> & { key: Key }) => {
    editform.setFieldsValue({ ...record });
    setEditingKey(record.key);
  };

  const cancel = () => {
    setEditingKey("");
  };

  const handleDelete = (key: React.Key) => {
    const newData = table.filter((item) => item.key !== key);
    setTable(newData);
  };

  const save = async (key: Key) => {
    try {
      const row = (await editform.validateFields()) as IDataTable;

      const newData = [...table];
      const index = newData.findIndex((item) => key === item.key);
      if (index > -1) {
        const item = newData[index];
        newData.splice(index, 1, {
          ...item,
          ...row,
        });
        setTable(newData);
        setEditingKey("");
      } else {
        newData.push(row);
        setTable(newData);
        setEditingKey("");
      }
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  const columns = [
    {
      width: "21%",
      title: "Подрядчик",
      dataIndex: "contractor",
      placeholder: "Подрядчик",
      key: "contractor",
      editable: true,
      render: (contractor: string) => (
        <StyledTitle
          level={5}
          style={{
            margin: 0,
            color: "#4A5368",
            fontSize: "14px",
            fontWeight: "400",
          }}
        >
          {contractor}
        </StyledTitle>
      ),
    },
    {
      title: "Раздел РД",
      dataIndex: "partitions",
      editable: true,
      children: [
        {
          width: "12%",
          title: "Ответственный",
          dataIndex: "responsible",
          key: "responsible",
          editable: true,
          render: (responsible: string[]) =>
            responsible.map((item) => (
              <span
                style={{
                  margin: 0,
                  color: "#4A5368",
                  fontSize: "14px",
                  fontWeight: "400",
                }}
              >
                {`${item} `}
              </span>
            )),
        },
        {
          width: "12%",
          title: "Просмотр",
          dataIndex: "view",
          key: "view",
          editable: true,
          render: (view: string[]) =>
            view.map((item) => (
              <span
                style={{
                  margin: 0,
                  color: "#4A5368",
                  fontSize: "14px",
                  fontWeight: "400",
                }}
              >
                {`${item} `}
              </span>
            )),
        },
      ],
    },
    {
      width: "12.5%",
      title: "Телефон",
      placeholder: "Телефон",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      editable: true,
      render: (phoneNumber: string) => (
        <Typography.Title
          level={5}
          style={{
            margin: 0,
            color: "#4A5368",
            fontSize: "14px",
            fontWeight: "400",
          }}
        >
          {phoneNumber}
        </Typography.Title>
      ),
    },
    {
      width: "18.5%",
      title: "E-mail",
      placeholder: "E-mail",
      dataIndex: "email",
      key: "email",
      editable: true,
      render: (email: string) => (
        <StyledEmailWrapper>
          <CopyableText text={email} />
        </StyledEmailWrapper>
      ),
    },
    {
      title: "",
      dataIndex: "operation",
      render: (_: any, record: IDataTable) => {
        const editable = isEditing(record);
        return editable ? (
          <Form.Item
            style={{
              width: "20%",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Button
                type="link"
                style={{
                  padding: "0px 0px 0px 28px",
                }}
                htmlType="submit"
                onClick={() => save(record.key)}
              >
                Сохранить
              </Button>

              <Button
                type="link"
                style={{
                  color: "#F04B69",
                  padding: "0px 0px 0px 50px",
                }}
                onClick={cancel}
              >
                Отменить
              </Button>
            </div>
          </Form.Item>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "space-around",
            }}
          >
            <Button
              type="link"
              style={{
                padding: "4px 0px 4px 30px",
              }}
              onClick={() => {
                edit(record);
              }}
            >
              Редактировать
            </Button>

            <Button
              type="link"
              style={{
                color: "#F04B69",
                padding: "0px",
              }}
              onClick={(key) => handleDelete(record.key)}
            >
              Удалить
            </Button>
          </div>
        );
      },
    },
  ];

  const mergedColumns = (col: any) => {
    if (!col.editable) {
      return col;
    }

    const newCol = {
      ...col,
      onCell: (record: IDataTable) => ({
        record,

        inputType:
          col.dataIndex === "responsible" || col.dataIndex === "view"
            ? "select"
            : "input",
        dataIndex: col.dataIndex,
        placeholder: col.placeholder,
        title: col.title,
        editing: isEditing(record),
      }),
    };
    if (col.children) {
      newCol.children = col.children.map(mergedColumns);
    }
    return newCol;
  };

  const finalColumns = columns.map(mergedColumns);

  return (
    <Form form={editform} component={false}>
      <StyledTable
        components={{
          body: {
            cell: EditableCell,
          },
        }}
        scroll={{ y: 550 }}
        footer={() => (
          <div>
            {isOpenCreateRow && (
              <>
                <Form
                  form={createform}
                  layout="inline"
                  name="createForm"
                  onFinish={onFinish}
                >
                  <CreateWrapper className="create-wrapper">
                    <Form.Item name="contractor" style={{ width: "19%" }}>
                      <StyledInput type="text" placeholder="Подрядчик" />
                    </Form.Item>

                    <Form.Item name="responsible" style={{ width: "11%" }}>
                      <StyledSelect
                        style={{ color: "#4a5368" }}
                        mode="multiple"
                        allowClear={false}
                        suffixIcon={
                          <ArrowDown className="arrow-icon ant-select-suffix" />
                        }
                        placeholder="Раздел РД"
                        onChange={() => {}}
                        options={options}
                      />
                    </Form.Item>

                    <Form.Item name="view" style={{ width: "11%" }}>
                      <StyledSelect
                        style={{ color: "#4a5368" }}
                        mode="multiple"
                        allowClear={false}
                        suffixIcon={
                          <ArrowDown className="arrow-icon ant-select-suffix" />
                        }
                        placeholder="Раздел РД"
                        onChange={() => {}}
                        options={options}
                      />
                    </Form.Item>

                    <Form.Item name="phoneNumber" style={{ width: "11.8%" }}>
                      <StyledInput type="number" placeholder="Телефон" />
                    </Form.Item>

                    <Form.Item name="email" style={{ width: "16.7%" }}>
                      <StyledInput placeholder="E-mail" />
                    </Form.Item>

                    <Form.Item
                      style={{
                        width: "20%",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-around",
                        }}
                      >
                        <Button
                          type="link"
                          style={{
                            padding: "0px",
                          }}
                          htmlType="submit"
                        >
                          Сохранить
                        </Button>

                        <Button
                          type="link"
                          style={{
                            color: "#F04B69",
                            padding: "0px",
                          }}
                          onClick={closeModal}
                        >
                          Отменить
                        </Button>
                      </div>
                    </Form.Item>
                  </CreateWrapper>

                  <Divider style={{ margin: 0, background: "#e2e7ee" }} />
                </Form>
              </>
            )}

            <Button
              type="link"
              style={{
                padding: "4px 0px 4px 0px",
                margin: "8px 12px",
              }}
              onClick={() => {
                cancel();
                setIsOpenCreateRow(true);
              }}
            >
              Добавить
            </Button>
          </div>
        )}
        pagination={pagination}
        dataSource={table}
        columns={finalColumns as any}
      />
    </Form>
  );
};
